import { useEffect, useState } from "react";
import CreateUpdateMinigirdFormView from "./CreateUpdateMinigirdFormView/CreateUpdateMinigirdFormView";
import { MinigirdPatch, Minigrid } from "../../../types/minigridtypes";
import useMinigrid, { useSingleMinigrid } from "../../../hook";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import minigridAPI from "../../../service/minigridAPI";
import { InstallationCodeErrorBody, installationCodeErrorMessage, planInstallationCodeLink, withoutInstallationCode } from "../../../utils/installationCode";
import { validationInputCreationMinigrid, ValidationMingrid } from "../../../utils/validationCheck";

import { FileDtoIn } from "../../../../../common/types/files.types";
import { convertFileToFileDto } from "../../../../../common/utils/fileReader";
import { FileUrl } from "../../../../../common/components/molecule/FileListManager/FileListManager";
import FileAPI from "../../../../../services/fileAPI";



export interface CreateUpdateMinigridFormPresenterProps {
    isUpdate?: boolean;
}

export default function CreateUpdateMinigridFormPresenter({isUpdate }: CreateUpdateMinigridFormPresenterProps) {

  const [data, setData] = useState<MinigirdPatch>({})
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  // Un échec du rattachement juste après la création arrive par la
  // navigation vers l'édition du site créé (le site, lui, existe déjà).
  const navState = location.state as { installationCodeError?: string; installationCode?: string } | null
  const navInstallationCode = navState?.installationCode
  const [installationCodeError, setInstallationCodeError] = useState<string | undefined>(navState?.installationCodeError)
  // Rattachement en cours après l'enregistrement du site : le bouton reste
  // verrouillé jusqu'au bout (sinon un second clic relance tout).
  const [isLinking, setIsLinking] = useState(false)
  const [errorInput, setErrorInput] = useState<ValidationMingrid | undefined>(undefined)

  const { createMinigrid :{isLoading, mutate: create, isSuccess,},  } = useMinigrid() 
  const { singleMinigridQuery: singleMinigridData, updateMinigrid} =  useSingleMinigrid()
  const [imageList, setImageList] = useState<FileUrl[] | undefined>([])



  useEffect(() => {
    if (isSuccess) {
      setData({})
    }

  },[isSuccess])

  useEffect(() => {
  if (isUpdate && singleMinigridData.data) {
    const fromServer = singleMinigridData.data
    // Après un refus du rattachement, le site est relu : on garde le code
    // saisi pour que l'utilisateur le corrige au lieu de le retaper.
    setData((prev) => ({
      ...fromServer,
      // Code saisi conservé : après un refus (relecture du site) ou après une
      // création dont le rattachement a échoué (transmis par la navigation).
      minigrid_installation_code: fromServer.minigrid_installation_code || prev.minigrid_installation_code || navInstallationCode,
    }))
    if (singleMinigridData.data?.minigrid_tumbnail_url) {
      const FileUrl : FileUrl = new File([], 'file')
      setImageList( [
       {...FileUrl,
        type: "image",
        url: FileAPI.getFilesURL(singleMinigridData.data.minigrid_tumbnail_url)
      
       }
      ])
  }
}
}, [singleMinigridData.data, isUpdate, navInstallationCode])

  if (isUpdate && singleMinigridData.isLoading) {
    return <p>loading...</p>
  }


  // Pose le code par la route dédiée (le CRUD générique ne l'écrit jamais).
  // Renvoie le message d'erreur à afficher, ou undefined si c'est fait.
  const linkInstallationCode = async (minigridId: string, code: string): Promise<string | undefined> => {
    setIsLinking(true)
    try {
      await minigridAPI.setInstallationCode(minigridId, code)
      queryClient.invalidateQueries('minigrid')
      queryClient.invalidateQueries('allMinigridsByAccount')
      return undefined
    } catch (err) {
      const res = axios.isAxiosError(err) ? err.response : undefined
      const body = res?.data as InstallationCodeErrorBody | undefined
      // Le serveur ne donne le site concurrent que s'il est du même compte :
      // on peut donc relire son nom pour l'afficher.
      let conflictName: string | undefined
      if (body?.conflict_minigrid_id) {
        conflictName = await minigridAPI.getSingleMinigrid(body.conflict_minigrid_id)
          .then((mg) => mg.minigrid_name)
          .catch(() => undefined)
      }
      return installationCodeErrorMessage(res?.status, body, () => conflictName)
    } finally {
      setIsLinking(false)
    }
  }

  const onSubmit = async () => {
    
    //Check input 

    // Code d'installation : facultatif ; s'il est saisi il doit respecter le
    // format du contrat. Un site déjà relié garde son code.
    const savedCode = isUpdate ? singleMinigridData.data?.minigrid_installation_code : null
    const { wantedCode, error: codeFormatError } = planInstallationCodeLink(savedCode, data.minigrid_installation_code)
    if (codeFormatError) {
      setInstallationCodeError(codeFormatError)
      return
    }
    setInstallationCodeError(undefined)

    const isError = validationInputCreationMinigrid(data as Minigrid)

    if (isError) {
      setErrorInput(isError)
      return
    }
    setErrorInput(undefined)


    if (isUpdate && singleMinigridData.data && singleMinigridData.data.minigrid_id) {

      const filesList = await convertFileToFileDto(imageList || []) 
      updateMinigrid.mutate({
        minigridId: singleMinigridData.data?.minigrid_id || '',
        minigrid: withoutInstallationCode(data as Minigrid),
        files: filesList.length > 0 ? filesList : undefined,
      
        
        callbacKFunc: async ({error}) => {
          if (error) {
            console.log(error)
            setErrorMessage(error.response?.data.message)
          }else{
            const codeError = wantedCode
              ? await linkInstallationCode(singleMinigridData.data?.minigrid_id || '', wantedCode)
              : undefined
            if (codeError) {
              setInstallationCodeError(codeError)
              return
            }
            setData({})
            console.log("success")
            navigate(-1)
          }
        }
      })


    } else {

      const fileList : FileDtoIn[]  | undefined= await convertFileToFileDto(imageList || []) 
      create({minigrid: withoutInstallationCode(data),files: fileList,callbackFunc: async ({error, result}) => {

        if (error) {
          setErrorMessage(error.response?.data.message)
          console.log(error)
          
        }else{
          const createdId = (result as { minigrid_id?: string } | undefined)?.minigrid_id
          const codeError = wantedCode && createdId ? await linkInstallationCode(createdId, wantedCode) : undefined
          setData({})
          if (codeError && createdId) {
            // replace : le retour arrière ne doit pas rouvrir un formulaire de
            // création vierge (le site existe déjà).
            navigate(`/main/minigrid/${createdId}/edit`, {
              replace: true,
              state: { installationCodeError: `Minigrid créé, mais : ${codeError}`, installationCode: wantedCode },
            })
            return
          }
          console.log("success")

          navigate("/main/minigrid")
        }
      }})
    }
  }



  
  return (
    <CreateUpdateMinigirdFormView
      error={errorMessage}
      onSubmit={onSubmit}
      setData={setData}
      data={data}
      labelSubmitBtn={(isUpdate ? "Modifier" : "Créer") + " le minigrid"}
      isLoading={ (isUpdate ? (singleMinigridData.isLoading || updateMinigrid.isLoading) :isLoading) || isLinking }
      isErrorInput={errorInput}
      setImages={setImageList}
      imageList={imageList}
      currentMinigridId={isUpdate ? singleMinigridData.data?.minigrid_id || null : null}
      savedInstallationCode={isUpdate ? singleMinigridData.data?.minigrid_installation_code : null}
      installationCodeError={installationCodeError}
    />
  )
}
